import { useEffect, useState } from "react";
import { ChevronDown, Zap } from "lucide-react";

/**
 * EditorSection — moderne, kollabierbare Sektion für den Inhalte-Editor.
 *
 * Props:
 *   icon: Lucide-Icon-Component (Pflicht)
 *   title: Sektion-Titel
 *   subtitle: optionale Kurzbeschreibung (klein, unter Titel)
 *   defaultOpen: bool (default true für Quick-Felder, false für Pro)
 *   quick: bool — Markierung als "im Schnell-Modus sichtbar" (zeigt Blitz-Badge)
 *   children: Inhalt (Form-Felder)
 *   testid: data-testid-Suffix
 */
export const EditorSection = ({
  icon: Icon,
  title,
  subtitle,
  defaultOpen = true,
  quick = false,
  children,
  testid = "",
  headerAction = null,
  enabled,           // bool | undefined  — wenn definiert: Toggle wird angezeigt
  onToggleEnabled,   // (newValue: bool) => void
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const toggleVisible = typeof enabled === "boolean" && typeof onToggleEnabled === "function";
  const isOff = toggleVisible && enabled === false;

  // Listener: wenn die TOC einen Anker anspringt, soll die Section auch
  // automatisch aufklappen (sonst sieht der User nur den Header).
  useEffect(() => {
    if (!testid) return;
    const handler = (e) => {
      if (e.detail?.testid === testid) setOpen(true);
    };
    window.addEventListener("editor-section-open", handler);
    return () => window.removeEventListener("editor-section-open", handler);
  }, [testid]);

  return (
    <section
      id={testid ? `editor-section-${testid}` : undefined}
      className={`bg-card border border-border rounded-lg overflow-visible transition-shadow hover:shadow-sm scroll-mt-32 ${isOff ? "opacity-60" : ""}`}
      data-testid={testid ? `editor-section-${testid}` : undefined}
    >
      {/* WICHTIG: dies darf KEIN <button> sein, weil im Inneren ein Switch-
          <button> sowie optional headerAction-Buttons stecken. Verschachtelte
          <button>-Tags sind nicht valide HTML → React Hydration-Error +
          „<button> cannot contain a nested <button>" Console-Warnung.
          Daher: <div role="button"> mit Keyboard-Handler. */}
      <div
        role="button"
        tabIndex={0}
        onClick={() => {
          setOpen(o => {
            const next = !o;
            // Beim Aufklappen: Live-Preview soll zur entsprechenden Section scrollen
            // bzw. (für impressum/datenschutz) auf die richtige Sub-Page wechseln.
            if (next && testid) {
              try {
                window.dispatchEvent(new CustomEvent("editor-section-open", { detail: { testid } }));
              } catch {}
            }
            return next;
          });
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            e.currentTarget.click();
          }
        }}
        className={`w-full flex items-center gap-3 px-4 py-3.5 text-left transition-all cursor-pointer select-none focus:outline-none focus:ring-2 focus:ring-primary/40 ${
          open
            ? "sticky top-0 z-20 bg-card border-b border-border/60 shadow-sm hover:bg-muted/20 rounded-t-lg"
            : "hover:bg-muted/30 rounded-lg"
        }`}
        data-testid={testid ? `editor-section-toggle-${testid}` : undefined}
      >
        <span className="w-8 h-8 rounded-md bg-primary/10 text-primary flex items-center justify-center shrink-0">
          {Icon && <Icon size={15} />}
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <h3 className="text-[13px] font-bold tracking-tight">{title}</h3>
            {quick && (
              <span
                className="inline-flex items-center gap-0.5 text-[9px] font-bold uppercase tracking-wider text-amber-700 dark:text-amber-400 bg-amber-100 dark:bg-amber-950/40 px-1.5 py-0.5 rounded-sm"
                title="Wichtig — wird bei jedem Demo angepasst"
              >
                <Zap size={9} className="-ml-0.5" /> Wichtig
              </span>
            )}
          </div>
          {subtitle && (
            <p className="text-[11px] text-muted-foreground mt-0.5 truncate">{subtitle}</p>
          )}
        </div>
        {headerAction && (
          <div onClick={(e) => e.stopPropagation()} className="shrink-0 mr-1">
            {headerAction}
          </div>
        )}
        {toggleVisible && (
          <div
            onClick={(e) => e.stopPropagation()}
            className="shrink-0 mr-2"
            data-testid={testid ? `editor-section-toggle-enabled-${testid}` : undefined}
          >
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={() => onToggleEnabled(!enabled)}
              title={enabled ? "Section wird angezeigt — klicken zum Ausblenden" : "Section ist ausgeblendet — klicken zum Anzeigen"}
              className={`relative inline-flex items-center h-5 w-9 rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/50 ${enabled ? "bg-emerald-500" : "bg-muted-foreground/30"}`}
            >
              <span
                className={`inline-block h-4 w-4 bg-white rounded-full shadow transform transition-transform ${enabled ? "translate-x-4" : "translate-x-0.5"}`}
              />
            </button>
          </div>
        )}
        <ChevronDown
          size={16}
          className={`text-muted-foreground shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </div>
      {open && (
        <div className="px-4 pb-4 pt-1 border-t border-border/60 space-y-3" data-testid={testid ? `editor-section-content-${testid}` : undefined}>
          {children}
        </div>
      )}
    </section>
  );
};
